import React, { useEffect, useState } from "react";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Undo2, Redo2, Save, Eye, RotateCcw, FileText, Loader2, Tag,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { useToast } from "../../hooks/use-toast";
import { MergeField } from "./MergeField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TbBtn = ({ onClick, active, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
      active ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
    }`}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-slate-200 mx-1" />;

const ContratoEditor = () => {
  const { toast } = useToast();
  const [campos, setCampos] = useState([]);
  const [padrao, setPadrao] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      MergeField,
    ],
    content: "",
    editorProps: { attributes: { class: "contrato-prose", "data-testid": "contrato-editor-area" } },
  });

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/contrato/modelo`);
        if (!ativo) return;
        setCampos(data.campos || []);
        setPadrao(data.padrao || "");
        if (editor) editor.commands.setContent(data.html || data.padrao || "");
      } catch (e) {
        toast({ title: "Erro ao carregar o modelo", description: e?.response?.data?.detail || "" });
      } finally {
        if (ativo) setLoading(false);
      }
    })();
    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const inserir = (field, chip) => {
    if (!editor) return;
    editor.chain().focus().insertContent({ type: "mergeField", attrs: { field, label: chip } }).run();
  };

  const salvar = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      await axios.put(`${API}/contrato/modelo`, { html: editor.getHTML() });
      toast({ title: "Modelo de contrato salvo", description: "Será usado ao gerar o contrato de cada residente." });
    } catch (e) {
      toast({ title: "Erro ao salvar", description: e?.response?.data?.detail || "Tente novamente." });
    } finally {
      setSaving(false);
    }
  };

  const preVisualizar = async () => {
    if (!editor) return;
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${API}/contrato/modelo/preview`,
        { html: editor.getHTML() },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: "Erro na pré-visualização", description: e?.response?.data?.detail || "" });
    } finally {
      setPreviewing(false);
    }
  };

  const restaurar = () => {
    if (!editor || !padrao) return;
    editor.commands.setContent(padrao);
    toast({ title: "Modelo padrão restaurado", description: "Clique em Salvar para confirmar a alteração." });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center" data-testid="contrato-editor-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando modelo…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="contrato-editor">
      <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 flex items-start gap-2">
        <Tag className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-600 leading-relaxed">
          Os campos em <span className="mf" style={{ fontSize: 10 }}>azul</span> são preenchidos automaticamente
          com os dados de cada residente e não podem ser editados — você pode apenas posicioná-los ou removê-los.
          Use os botões <b>“Inserir campo”</b> para adicioná-los ao texto.
        </p>
      </div>

      {/* Paleta de campos */}
      <div className="flex flex-wrap gap-2" data-testid="contrato-campos-paleta">
        {campos.map((g) => (
          <DropdownMenu key={g.grupo}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs" data-testid={`inserir-grupo-${g.grupo}`}>
                <Tag className="w-3.5 h-3.5 text-blue-600" /> {g.grupo}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              <DropdownMenuLabel className="text-xs">{g.grupo}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {g.itens.map((it) => (
                <DropdownMenuItem
                  key={it.key}
                  onClick={() => inserir(it.key, it.chip)}
                  data-testid={`inserir-campo-${it.key}`}
                  className="text-sm cursor-pointer"
                >
                  {it.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
      </div>

      {/* Editor */}
      <div className="contrato-editor rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        {/* Toolbar */}
        <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-slate-100 bg-slate-50/80 sticky top-0 z-10">
          <TbBtn testid="fmt-bold" title="Negrito" active={editor?.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-italic" title="Itálico" active={editor?.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-underline" title="Sublinhado" active={editor?.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="w-4 h-4" /></TbBtn>
          <Sep />
          <TbBtn testid="fmt-h1" title="Título 1" active={editor?.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-h2" title="Título 2" active={editor?.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-h3" title="Título 3" active={editor?.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></TbBtn>
          <Sep />
          <TbBtn testid="fmt-ul" title="Lista" active={editor?.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-ol" title="Lista numerada" active={editor?.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></TbBtn>
          <Sep />
          <TbBtn testid="fmt-align-left" title="Esquerda" active={editor?.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-align-center" title="Centralizar" active={editor?.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-align-right" title="Direita" active={editor?.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-align-justify" title="Justificar" active={editor?.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}><AlignJustify className="w-4 h-4" /></TbBtn>
          <Sep />
          <TbBtn testid="fmt-undo" title="Desfazer" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-4 h-4" /></TbBtn>
          <TbBtn testid="fmt-redo" title="Refazer" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-4 h-4" /></TbBtn>
        </div>
        <EditorContent editor={editor} />
      </div>

      {/* Ações */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" className="gap-2 text-slate-600" onClick={restaurar} data-testid="contrato-restaurar-btn">
          <RotateCcw className="w-4 h-4" /> Restaurar modelo padrão
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={preVisualizar} disabled={previewing} data-testid="contrato-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Pré-visualizar
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar} disabled={saving} data-testid="contrato-salvar-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar modelo
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
        <FileText className="w-3.5 h-3.5" /> O cabeçalho (logo/empresa) e o rodapé com paginação são adicionados automaticamente ao PDF.
      </p>
    </div>
  );
};

export default ContratoEditor;
